import { Token, decimalsToWei } from '@akropolis-web/primitives';
import React, { useCallback } from 'react';
import { Box } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { ChartsAxisContentProps, axisClasses } from '@mui/x-charts';

import { getAmount } from 'domain/utils';
import { schemas } from 'api/modules/apostro-rest/types';

import { LiqLevelsTooltip } from './LiqLevelsTooltip';

export type LiqLevelData = {
  liqPrice: number;
  type: schemas['LiqType'];
  marketLiqVolume: number;
  othersLiqVolume: number;
};

type LiqLevelsChartProps = {
  liqLevels: LiqLevelData[];
  currentToken: Token;
  marketName: string;
  liqType: schemas['LiqType'];
};

export function LiqLevelsChart({
  liqLevels,
  currentToken,
  marketName,
  liqType,
}: LiqLevelsChartProps) {
  const valueFormatter = (value: number, precision: number) => {
    const { formatted } = getAmount(value, '$')
      .mul(decimalsToWei(18))
      .toFormattedBalance(precision, true, true);
    return formatted.replace(/^(\$\d+)(\.0+?)?([^\d])?$/, '$1$3');
  };

  const tickStep = 25;
  const visibleTicks = new Array(Math.floor(liqLevels.length / tickStep) + 1)
    .fill(null)
    .map((_, index) => liqLevels[Math.max(0, index * tickStep - 1)].liqPrice);

  const AxisContent = useCallback(
    (props: ChartsAxisContentProps) => (
      <LiqLevelsTooltip data={props} marketName={marketName} liqType={liqType} />
    ),
    [liqType, marketName],
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
      <BarChart
        height={240}
        dataset={liqLevels}
        series={[
          {
            dataKey: 'othersLiqVolume',
            label: 'All money markets',
            stackOrder: 'reverse',
            stack: 'total',
            highlightScope: {
              highlighted: 'item',
            },
            color: '#2B94F5',
          },
          {
            dataKey: 'marketLiqVolume',
            stackOrder: 'reverse',
            label: marketName,
            stack: 'total',
            highlightScope: {
              highlighted: 'item',
            },
            color: '#41996B',
          },
        ]}
        xAxis={[
          {
            tickLabelInterval: value => visibleTicks.includes(value),
            dataKey: 'liqPrice',
            scaleType: 'band',
            id: 'axis1',
            label: `${currentToken.symbol} price`,
            valueFormatter: (value: number) => valueFormatter(value, 2),
          },
        ]}
        yAxis={[
          {
            label: 'Liquidation volume',
            valueFormatter: (value: number) => valueFormatter(value, 1),
          },
        ]}
        slots={{
          axisContent: AxisContent,
        }}
        slotProps={{
          legend: {
            direction: 'column',
            position: { vertical: 'top', horizontal: 'left' },
            labelStyle: {
              fontSize: 12,
            },
            itemMarkWidth: 12,
            itemMarkHeight: 12,
            itemGap: 6,
            padding: 0,
          },
        }}
        margin={{ top: 5, right: 5, bottom: 45, left: 72 }}
        sx={{
          [`.${axisClasses.left} .${axisClasses.label}`]: {
            transform: 'translate(-35px, 0)',
          },
          '& .MuiChartsLegend-root': {
            transform: 'translate(90px, 0)',
          },
          '& .MuiChartsAxis-directionX .MuiChartsAxis-tick': {
            display: 'none',
          },
          [`& .MuiChartsAxis-directionX .MuiChartsAxis-tickContainer:nth-of-type(${tickStep}n+1) .MuiChartsAxis-tick`]:
            {
              display: 'block',
            },
        }}
      />
    </Box>
  );
}
